import {
  FunctionComponent,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { StringIndexedObject, useToast } from '@kanda-libs/ks-design-library';
import {
  Credit,
  Document,
  Job,
  JobCompanyInfo,
  Service,
  SignDocument,
  Signature,
  actions,
  services,
  useSubmit,
} from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
import useApiError from 'hooks/useApiError';
import { useStatusContext } from 'pages/Status/context';
import { reloadInitialData } from 'store/slices/app';
import { extractErrorMessage, getJobType } from 'utils';

export type DocumentPage =
  | 'preContact'
  | 'creditAgreement'
  | 'directDebit'
  | 'success';

export interface OmniSignDocumentsProps {
  children: ReactNode;
}

export interface OmniSignDocumentsContextType {
  job?: Job;
  credit?: Credit;
  quotePpfB64?: string;
  documents?: Document[];
  page: DocumentPage;
  setPage: React.Dispatch<React.SetStateAction<DocumentPage>>;
  signDocuments: (body: Signature) => void;
  isLoading: boolean;
  isFetching: boolean;
  isSubmitting: boolean;
}

export const getFileName = (job: Job, type: string): string =>
  `kanda-job${job?.id ? `-${job.id}` : ''}-${type}.pdf`;

export const OmniSignDocumentsContext =
  createContext<OmniSignDocumentsContextType>({
    job: undefined,
    credit: undefined,
    quotePpfB64: undefined,
    documents: undefined,
    page: 'preContact',
    setPage: () => {},
    signDocuments: () => {},
    isLoading: false,
    isFetching: false,
    isSubmitting: false,
  });

export const useOmniSignDocumentsContext = () =>
  useContext(OmniSignDocumentsContext);

const OmniSignDocumentsProvider: FunctionComponent<OmniSignDocumentsProps> =
  function ({ children }) {
    const [page, setPage] = useState<DocumentPage>('preContact');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [documents, setDocuments] = useState<Document[] | undefined>([]);
    const [quotePpfB64, setQuotePpfB64] = useState<string | undefined>();
    const [documentsIsFetching, setDocumentsIsFetching] =
      useState<boolean>(false);
    const [pdfIsFetching, setPdfIsFetching] = useState<boolean>(false);
    const isFetching = documentsIsFetching || pdfIsFetching;

    const { job, credit, jobCompanyInfo, isLoading } = useStatusContext();
    // const documents = useSelector(selectors.document.getEntitiesAsArray);
    const dispatch = useAppDispatch();
    const onCheckError = useApiError(
      'Error checking credit - please contact Ideal4Finance',
    );
    const onFetchError = useApiError(
      'Error fetching documents - please contact Ideal4Finance',
    );
    const onPdfFetchError = useApiError(
      'Error fetching quote PDF - please contact Ideal4Finance',
    );
    const onSignError = useApiError(
      'Error signing contracts - please contact Ideal4Finance',
    );
    const { showError } = useToast();

    const { submit: createQuotePdf } = useSubmit(
      services.pdf.create as unknown as Service<
        StringIndexedObject,
        StringIndexedObject,
        StringIndexedObject
      >,
    );

    const signDocuments = useCallback(
      (signature: Signature) => {
        if (!credit || !job) {
          showError(
            'Error getting job or credit from state - please refresh and try again',
          );
          return;
        }
        const id = credit?.id;
        if (!id) {
          showError(
            'Cannot extract credit id to sign documents - please refresh and try again',
          );
          return;
        }
        setIsSubmitting(true);
        const body: SignDocument = {
          signature,
          quote: {
            name: getFileName(job, 'quote'),
            content: quotePpfB64,
          },
        };
        dispatch(
          actions.signCredit({
            body,
            params: { id },
            onError: (error) => {
              setIsSubmitting(false);
              onSignError(error);
            },
            onSuccess: () => {
              setIsSubmitting(false);
              reloadInitialData();
              setPage('success');
            },
          }),
        );
      },
      [credit, job, dispatch, showError, onSignError, quotePpfB64],
    );

    const fetchDocs = useCallback(
      async (creditToFetch: Credit) => {
        // Fetch documents then setIsFetching false
        const docs = creditToFetch?.credit_documents;
        if (!docs) {
          showError(
            'Error - credit has no credit documents attached. Please refresh the page. If this issue persists, contact Ideal4Finance',
          );
          setDocumentsIsFetching(false);
          return;
        }
        const ids = docs
          .map((doc: Document) => doc?.id)
          .filter(Boolean) as string[];
        const fetchedDocuments: Document[] = [];
        await Promise.all(
          ids.map((id: string) =>
            dispatch(
              actions.getDocument({
                params: { id },
                onError: onFetchError,
                onSuccess: (data: Document) => fetchedDocuments.push(data),
              }),
            ),
          ),
        );
        setDocuments(fetchedDocuments);
        setDocumentsIsFetching(false);
      },
      [showError, onFetchError, dispatch],
    );

    const checkCreditAndFetchDocs = useCallback(
      (id: string) => {
        dispatch(
          actions.checkCredit({
            params: { id },
            onError: onCheckError,
            onSuccess: (checkedCredit: Credit) => fetchDocs(checkedCredit),
          }),
        );
      },
      [fetchDocs, dispatch, onCheckError],
    );

    const getCustomQuotePdf = useCallback(
      (customJob: Job) => {
        const id = customJob?.job_items?.[0]?.quote_document?.id;
        if (!id) {
          setPdfIsFetching(false);
          showError('No quote attached to job - please contact Ideal4Finance');
          return;
        }
        dispatch(
          actions.getDocument({
            params: { id },
            onError: onPdfFetchError,
            onSuccess: (data: Document) => {
              setPdfIsFetching(false);
              setQuotePpfB64(data.content);
            },
          }),
        );
      },
      [dispatch, onPdfFetchError, showError],
    );

    const getKandaQuotePdf = useCallback(
      async (kandaJob: Job, company: JobCompanyInfo | undefined) => {
        if (!company) {
          setPdfIsFetching(false);
          showError(
            'No company info attached to job - please contact Ideal4Finance',
          );
          return;
        }
        createQuotePdf({
          body: {
            job: kandaJob,
            company,
          },
        }).then(({ data: pdf, error: pdfError }) => {
          setPdfIsFetching(false);
          if (pdfError || !pdf) {
            const message = extractErrorMessage(pdfError as string);
            showError(
              message || 'Error fetching job quote document at this time',
            );
            return;
          }
          setQuotePpfB64(pdf.base64);
        });
      },
      [createQuotePdf, showError],
    );

    const fetchRef = useRef<boolean>(false);
    useEffect(() => {
      if (isLoading || isFetching || !credit || fetchRef.current) return;
      const id = credit?.id;
      if (!id) return;
      fetchRef.current = true;
      setDocumentsIsFetching(true);
      const docs = credit?.credit_documents;
      if (!docs) {
        checkCreditAndFetchDocs(id);
        return;
      }
      fetchDocs(credit);
    }, [isLoading, isFetching, credit, checkCreditAndFetchDocs, fetchDocs]);

    const quotePdfRef = useRef<boolean>(false);
    useEffect(() => {
      if (
        isLoading ||
        pdfIsFetching ||
        !job ||
        !jobCompanyInfo ||
        quotePdfRef.current
      )
        return;
      const jobType = getJobType(job);
      if (!jobType) return;
      quotePdfRef.current = true;
      setPdfIsFetching(true);
      if (jobType === 'custom') {
        getCustomQuotePdf(job);
        return;
      }
      getKandaQuotePdf(job, jobCompanyInfo);
    }, [
      isLoading,
      pdfIsFetching,
      job,
      jobCompanyInfo,
      getCustomQuotePdf,
      getKandaQuotePdf,
    ]);

    const values = useMemo(
      () => ({
        job,
        credit,
        documents,
        quotePpfB64,
        page,
        setPage,
        signDocuments,
        isLoading,
        isFetching,
        isSubmitting,
      }),
      [
        job,
        credit,
        documents,
        quotePpfB64,
        page,
        setPage,
        signDocuments,
        isLoading,
        isFetching,
        isSubmitting,
      ],
    );

    return (
      <OmniSignDocumentsContext.Provider value={values}>
        {children}
      </OmniSignDocumentsContext.Provider>
    );
  };

export default OmniSignDocumentsProvider;
